import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getNewsById, newsList } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const item = id ? getNewsById(id) : undefined;

  useSeo({
    title: item ? `${item.title} - 顽皮的女朋友影视资讯` : "影视资讯 - 顽皮的女朋友",
    description: item?.summary,
    keywords: item ? `${item.category},影视资讯,${item.title}` : undefined,
  });

  const related = useMemo(() => {
    if (!item) return [];
    return newsList.filter((news) => news.id !== item.id).slice(0, 3);
  }, [item]);

  if (!item) {
    return (
      <div className="px-4 md:px-6 py-24 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-semibold text-xl text-foreground-950">
          未找到该资讯
        </h1>
        <p className="mt-2 text-sm text-foreground-600">该资讯可能已被移除，或链接有误。</p>
        <Link
          to="/news"
          className="mt-5 h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-heading text-sm tracking-wide hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视资讯", to: "/news" },
          { label: item.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
        <article className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-label tracking-wide">
            {item.category}
          </span>
          <h1 className="mt-4 font-heading font-bold text-2xl md:text-4xl leading-snug text-foreground-950">
            {item.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {item.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {item.date}
            </span>
          </div>

          <div className="mt-6 rounded-lg overflow-hidden border border-background-200/70">
            <img
              src={item.cover}
              alt={`${item.title}配图`}
              title={`${item.title}影视资讯配图`}
              className="w-full aspect-[12/7] object-cover object-top"
            />
          </div>

          <div className="mt-7 space-y-4">
            {item.content.map((paragraph, index) => (
              <p
                key={index}
                className="text-sm md:text-base leading-relaxed text-foreground-700"
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-9 pt-6 border-t border-background-200/70 flex flex-wrap items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="h-11 px-5 inline-flex items-center gap-2 rounded-md border border-background-300 text-foreground-700 font-heading text-sm tracking-wide hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回上一页
            </button>
            <Link
              to="/news"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              查看全部资讯 →
            </Link>
          </div>
        </article>

        <aside className="rounded-lg bg-background-100 border border-background-200/70 p-5 lg:sticky lg:top-24">
          <h2 className="font-heading font-semibold text-base text-foreground-950 flex items-center gap-2">
            <i className="ri-article-line text-primary-600"></i>
            相关文章
          </h2>
          <ul className="mt-4 space-y-4">
            {related.map((news) => (
              <li key={news.id}>
                <Link
                  to={`/news/${news.id}`}
                  className="group block"
                >
                  <p className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {news.title}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">{news.date}</p>
                </Link>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}